import styled, { keyframes } from 'styled-components';

export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  padding: 16px 20px 40px 20px;
  gap: 20px;
  font-family: 'Montserrat', sans-serif;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  background-color: transparent;
`;

export const TopHeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 8px 0 16px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)'};
`;

export const HeaderTitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h1 {
    font-size: 1.55rem;
    font-weight: 800;
    color: ${(props) => (props.theme.name === 'light' ? '#001f5c' : '#ffffff')};
    margin: 0;
    letter-spacing: -0.3px;
    display: flex;
    align-items: center;
    gap: 10px;

    span.badge {
      font-size: 0.72rem;
      font-weight: 700;
      padding: 3px 10px;
      border-radius: 9999px;
      background: ${(props) =>
        props.theme.name === 'light'
          ? 'rgba(2, 132, 199, 0.12)'
          : 'rgba(166, 193, 11, 0.18)'};
      color: ${(props) =>
        props.theme.name === 'light' ? '#0369a1' : '#a6c10b'};
      border: 1px solid
        ${(props) =>
          props.theme.name === 'light'
            ? 'rgba(2, 132, 199, 0.3)'
            : 'rgba(166, 193, 11, 0.35)'};
      letter-spacing: 0.5px;
    }
  }

  p {
    font-size: 0.85rem;
    color: ${(props) =>
      props.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.65)'};
    margin: 0;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const DownloadPptxButton = styled.button<{ $isDownloading?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 22px;
  border-radius: 12px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.88rem;
  font-weight: 800;
  letter-spacing: 0.3px;
  cursor: ${(props) => (props.$isDownloading ? 'not-allowed' : 'pointer')};
  background: ${(props) =>
    props.theme.name === 'light'
      ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
      : 'linear-gradient(135deg, #a6c10b 0%, #8ea709 100%)'};
  color: ${(props) => (props.theme.name === 'light' ? '#ffffff' : '#001842')};
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 4px 14px rgba(2, 132, 199, 0.3)'
      : '0 4px 18px rgba(166, 193, 11, 0.4)'};
  transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  opacity: ${(props) => (props.$isDownloading ? 0.7 : 1)};

  &:hover {
    transform: ${(props) => (props.$isDownloading ? 'none' : 'translateY(-2px)')};
    box-shadow: ${(props) =>
      props.theme.name === 'light'
        ? '0 6px 20px rgba(2, 132, 199, 0.45)'
        : '0 6px 24px rgba(166, 193, 11, 0.6)'};
  }

  &:active {
    transform: scale(0.97);
  }
`;

export const FilterCardWrapper = styled.div`
  background: ${(props) =>
    props.theme.name === 'light'
      ? '#ffffff'
      : 'linear-gradient(135deg, #00205b 0%, #002b80 60%, #001842 100%)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 18px;
  padding: 20px 24px;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 4px 16px rgba(0, 0, 0, 0.05)'
      : '0 6px 24px rgba(0, 0, 0, 0.35)'};
  animation: ${fadeInUp} 0.3s ease;
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  animation: ${fadeInUp} 0.35s ease both;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const KpiCard = styled.div<{ $accentColor?: string }>`
  background: ${(props) =>
    props.theme.name === 'light'
      ? '#ffffff'
      : 'linear-gradient(135deg, #00205b 0%, #002b80 100%)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light'
        ? '#e2e8f0'
        : 'rgba(255, 255, 255, 0.1)'};
  border-left: 4px solid
    ${(props) => props.$accentColor || (props.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 4px 14px rgba(0, 0, 0, 0.05)'
      : '0 4px 20px rgba(0, 0, 0, 0.3)'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.theme.name === 'light'
        ? '0 6px 20px rgba(0, 0, 0, 0.08)'
        : '0 6px 26px rgba(0, 0, 0, 0.4)'};
  }
`;

export const KpiCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  span.label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: ${(props) =>
      props.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.6)'};
  }

  span.badge {
    font-size: 0.68rem;
    font-weight: 800;
    padding: 2px 8px;
    border-radius: 9999px;
  }
`;

export const KpiCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h2.main-val {
    font-size: 1.65rem;
    font-weight: 800;
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    margin: 0;
    line-height: 1.1;
  }

  span.sub-val {
    font-size: 0.82rem;
    font-weight: 600;
    color: ${(props) =>
      props.theme.name === 'light' ? '#475569' : 'rgba(255, 255, 255, 0.7)'};
  }
`;

export const KpiCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.72rem;
  padding-top: 8px;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)'};
  color: ${(props) =>
    props.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.55)'};

  strong {
    color: ${(props) =>
      props.theme.name === 'light' ? '#0f172a' : '#ffffff'};
  }
`;

export const SubTabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 2px solid
    ${(props) =>
      props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)'};
  padding-bottom: 2px;
  overflow-x: auto;
`;

export const SubTabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px 10px 0 0;
  border: none;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  font-weight: ${(props) => (props.$active ? 700 : 600)};
  transition: all 0.2s ease;
  background: ${(props) =>
    props.$active
      ? props.theme.name === 'light'
        ? '#0284c7'
        : 'linear-gradient(135deg, #a6c10b 0%, #8ea709 100%)'
      : 'transparent'};
  color: ${(props) =>
    props.$active
      ? props.theme.name === 'light'
        ? '#ffffff'
        : '#001842'
      : props.theme.name === 'light'
      ? '#64748b'
      : 'rgba(255, 255, 255, 0.6)'};

  &:hover {
    color: ${(props) =>
      props.$active
        ? props.theme.name === 'light'
          ? '#ffffff'
          : '#001842'
        : props.theme.name === 'light'
        ? '#0f172a'
        : '#ffffff'};
    background: ${(props) =>
      props.$active
        ? undefined
        : props.theme.name === 'light'
        ? 'rgba(0,0,0,0.04)'
        : 'rgba(255,255,255,0.05)'};
  }
`;

export const TableCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'light'
      ? '#ffffff'
      : 'linear-gradient(135deg, #00205b 0%, #002b80 100%)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 18px;
  overflow: hidden;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 4px 16px rgba(0, 0, 0, 0.05)'
      : '0 6px 24px rgba(0, 0, 0, 0.35)'};
  animation: ${fadeInUp} 0.35s ease both;
`;

export const TableHeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.25)'};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)'};

  h3 {
    font-size: 0.95rem;
    font-weight: 800;
    color: ${(props) => (props.theme.name === 'light' ? '#001f5c' : '#ffffff')};
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  min-width: 240px;

  input {
    width: 100%;
    background: ${(props) =>
      props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 0, 0, 0.3)'};
    border: 1px solid
      ${(props) =>
        props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'};
    border-radius: 10px;
    padding: 8px 12px 8px 34px;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.82rem;
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    outline: none;

    &:focus {
      border-color: ${(props) =>
        props.theme.name === 'light' ? '#0284c7' : '#a6c10b'};
    }
  }

  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${(props) =>
      props.theme.name === 'light' ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)'};
    font-size: 14px;
    pointer-events: none;
  }
`;

export const ResponsiveTable = styled.div`
  width: 100%;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Montserrat', sans-serif;
    text-align: left;

    thead tr {
      background: ${(props) =>
        props.theme.name === 'light' ? '#f1f5f9' : 'rgba(0, 0, 0, 0.35)'};
      border-bottom: 2px solid
        ${(props) =>
          props.theme.name === 'light'
            ? '#cbd5e1'
            : 'rgba(255, 255, 255, 0.15)'};

      th {
        padding: 12px 16px;
        font-size: 0.72rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.6px;
        color: ${(props) =>
          props.theme.name === 'light' ? '#475569' : '#a6c10b'};
        white-space: nowrap;
      }
    }

    tbody tr {
      border-bottom: 1px solid
        ${(props) =>
          props.theme.name === 'light'
            ? '#f1f5f9'
            : 'rgba(255, 255, 255, 0.05)'};
      transition: background 0.15s ease;

      &:hover {
        background: ${(props) =>
          props.theme.name === 'light'
            ? '#f8fafc'
            : 'rgba(255, 255, 255, 0.04)'};
      }

      td {
        padding: 12px 16px;
        font-size: 0.82rem;
        color: ${(props) =>
          props.theme.name === 'light' ? '#1e293b' : 'rgba(255, 255, 255, 0.9)'};
        white-space: nowrap;
      }
    }
  }
`;

export const TablePaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.2)'};
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)'};
  font-size: 0.78rem;
  color: ${(props) =>
    props.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.6)'};

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    button {
      padding: 6px 14px;
      border-radius: 8px;
      border: 1px solid
        ${(props) =>
          props.theme.name === 'light'
            ? '#cbd5e1'
            : 'rgba(255, 255, 255, 0.2)'};
      background: ${(props) =>
        props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)'};
      color: ${(props) =>
        props.theme.name === 'light' ? '#0f172a' : '#ffffff'};
      font-size: 0.75rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.15s ease;

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      &:not(:disabled):hover {
        background: ${(props) =>
          props.theme.name === 'light' ? '#f1f5f9' : '#001842'};
        border-color: ${(props) =>
          props.theme.name === 'light' ? '#0284c7' : '#a6c10b'};
      }
    }
  }
`;

export const StatusPill = styled.span<{ $type: 'baixo' | 'adequado' | 'excesso' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.3px;

  ${(props) => {
    switch (props.$type) {
      case 'baixo':
        return `
          background: ${props.theme.name === 'light' ? '#fef2f2' : 'rgba(239, 68, 68, 0.18)'};
          color: ${props.theme.name === 'light' ? '#dc2626' : '#f87171'};
          border: 1px solid ${props.theme.name === 'light' ? '#fecaca' : 'rgba(239, 68, 68, 0.35)'};
        `;
      case 'adequado':
        return `
          background: ${props.theme.name === 'light' ? '#ecfdf5' : 'rgba(16, 185, 129, 0.18)'};
          color: ${props.theme.name === 'light' ? '#059669' : '#34d399'};
          border: 1px solid ${props.theme.name === 'light' ? '#a7f3d0' : 'rgba(16, 185, 129, 0.35)'};
        `;
      case 'excesso':
        return `
          background: ${props.theme.name === 'light' ? '#eff6ff' : 'rgba(59, 130, 246, 0.18)'};
          color: ${props.theme.name === 'light' ? '#2563eb' : '#60a5fa'};
          border: 1px solid ${props.theme.name === 'light' ? '#bfdbfe' : 'rgba(59, 130, 246, 0.35)'};
        `;
      default:
        return `
          background: ${props.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.1)'};
          color: ${props.theme.name === 'light' ? '#475569' : '#e2e8f0'};
          border: 1px solid ${props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.2)'};
        `;
    }
  }}
`;
